/**
 * TenantContext - Provides tenant information throughout the app
 *
 * Resolves tenant from subdomain and fetches public config before rendering.
 * Used for:
 * - Theme loading before login
 * - Public page (santinho digital)
 * - Multi-tenant routing
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { TenantPublicConfig, DEFAULT_THEME, RESERVED_SLUGS } from '@/types/tenant.types';

// =============================================================================
// Types
// =============================================================================

interface TenantContextValue {
  // Tenant data
  tenant: TenantPublicConfig | null;
  slug: string | null;
  isLoading: boolean;
  error: string | null;

  // State checks
  isLandingPage: boolean; // On main domain (campanhaflow.com.br)
  isTenantPage: boolean; // On tenant subdomain (lula.campanhaflow.com.br)
  hasPublicPage: boolean; // Tenant has public page enabled

  // Actions
  refetch: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract tenant slug from current hostname
 */
function getTenantSlugFromHostname(): string | null {
  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for slug in query param for local dev
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant') || null;
  }

  // Extract subdomain from hostname
  // Supports both:
  // - Production: lula.campanhaflow.com.br -> lula
  // - Dev/Staging: lula.dev.campanhaflow.com.br -> lula
  // - campanhaflow.com.br -> null
  // - dev.campanhaflow.com.br -> null (reserved)

  // Match pattern: slug.campanhaflow.com.br OR slug.env.campanhaflow.com.br
  const prodMatch = hostname.match(/^([^.]+)\.campanhaflow\.com\.br$/);
  const envMatch = hostname.match(/^([^.]+)\.(dev|staging)\.campanhaflow\.com\.br$/);

  const match = prodMatch || envMatch;
  if (!match) return null;

  const slug = match[1];

  // Check reserved slugs
  if (RESERVED_SLUGS.includes(slug as (typeof RESERVED_SLUGS)[number])) {
    return null;
  }

  return slug;
}

/**
 * Fetch tenant public config from API
 */
async function fetchTenantConfig(slug: string): Promise<TenantPublicConfig> {
  // Use base URL without /api suffix for public endpoints
  const apiUrl = (import.meta.env.VITE_API_URL || '').replace(/\/api$/, '');
  const response = await fetch(`${apiUrl}/public/tenants/${slug}/config`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Tenant not found');
    }
    throw new Error('Failed to fetch tenant config');
  }

  const data = (await response.json()) as { data: TenantPublicConfig };
  return data.data;
}

// =============================================================================
// Provider Component
// =============================================================================

interface TenantProviderProps {
  children: React.ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantPublicConfig | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTenant = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const detectedSlug = getTenantSlugFromHostname();
    setSlug(detectedSlug);

    if (!detectedSlug) {
      // Main domain - no tenant to load
      setTenant(null);
      setIsLoading(false);
      return;
    }

    try {
      const config = await fetchTenantConfig(detectedSlug);
      setTenant(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTenant();
  }, [loadTenant]);

  // Apply theme CSS variables when tenant loads
  useEffect(() => {
    const theme = tenant?.theme || DEFAULT_THEME;
    const root = document.documentElement;

    root.style.setProperty('--color-primary', theme.primaryColor);
    root.style.setProperty('--color-secondary', theme.secondaryColor);

    // Update favicon if provided
    if (theme.faviconUrl) {
      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favicon) {
        favicon.href = theme.faviconUrl;
      }
    }
  }, [tenant]);

  const value: TenantContextValue = {
    tenant,
    slug,
    isLoading,
    error,
    isLandingPage: !slug,
    isTenantPage: !!slug,
    hasPublicPage: !!tenant?.publicPage.enabled,
    refetch: loadTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

// =============================================================================
// Additional Hooks
// =============================================================================

/**
 * Hook for just the tenant theme
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTenantTheme() {
  const { tenant } = useTenant();
  return tenant?.theme || DEFAULT_THEME;
}

/**
 * Hook for checking if we're on a specific tenant
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsTenant(expectedSlug: string): boolean {
  const { slug } = useTenant();
  return slug === expectedSlug;
}
